export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'nurse' | 'doctor' | 'caregiver' | 'viewer';
  facility: {
    id: string;
    name: string;
  };
}

export interface Patient {
  /** True when the whole patient record is fabricated for demonstration. */
  is_demo?: boolean;
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  room_number: string;
  facility_id?: string;
  facility_name: string;
  active_devices: number;
  active_alerts: number;
}

export interface Alert {
  id: string;
  patient_id: string;
  patient_name: string;
  room_number: string;
  facility_name: string;
  alert_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  status: 'active' | 'acknowledged' | 'resolved';
  created_at: string;
}

export interface Vital {
  id: string;
  vital_type: string;
  value: number;
  unit: string;
  systolic?: number;
  diastolic?: number;
  recorded_at: string;
  source: string;
  device_type?: string;
}

export interface Facility {
  id: string;
  name: string;
  address?: string;
  phone?: string;
}

export type DeviceType =
  | 'apple_watch'
  | 'oura'
  | 'emfit'
  | 'biostrap'
  | 'withings_bp'
  | 'withings_scale';

export interface Device {
  id: string;
  patient_id: string;
  patient_name: string;
  device_type: DeviceType | string;
  device_id: string;
  serial_number: string;
  status: 'active' | 'inactive' | 'maintenance';
  last_sync: string;
}

// Device capability mapping - which metrics each device type can provide
export const DEVICE_CAPABILITIES: Record<DeviceType, string[]> = {
  apple_watch: ['heart_rate', 'spo2', 'temperature', 'respiratory_rate', 'activity'],
  oura: ['heart_rate', 'hrv', 'temperature', 'respiratory_rate', 'sleep'],
  emfit: ['heart_rate', 'hrv', 'respiratory_rate', 'sleep', 'bed_presence'],
  biostrap: ['heart_rate', 'hrv', 'spo2', 'respiratory_rate', 'sleep'],
  withings_bp: ['blood_pressure', 'heart_rate'],
  withings_scale: ['weight', 'body_composition'],
};

// Visensia Health Index Types

/**
 * Health index score level (0-5 scale)
 * 0: Stable - all normal
 * 1: Minor deviation
 * 2: Moderate deviation
 * 3: Significant - clinical review recommended
 * 4: Major - urgent review required
 * 5: Critical - emergency response required
 */
export type HealthIndexLevel = 0 | 1 | 2 | 3 | 4 | 5;

export interface VitalContribution {
  vital_type: string;
  value: number;
  unit: string;
  weight: number;
  flagged: boolean;
  flag_reason?: string;
  systolic?: number;
  diastolic?: number;
}

export interface HealthIndexScore {
  id: string;
  patient_id: string;
  score: HealthIndexLevel;
  confidence: number;
  vital_contributions: VitalContribution[];
  vitals_window_start: string;
  vitals_window_end: string;
  readings_count: number;
  request_id: string;
  processing_time_ms: number;
  calculated_at: string;
  created_at: string;
  /** Raw response from the Visensia SDK (when available) */
  raw_response?: {
    rawScore?: number;
    alert?: boolean;
    isFinal?: boolean;
    anomalies?: Array<{ name: string; value: number }>;
  };
}

export interface HealthIndexDisplay {
  score: HealthIndexLevel;
  label: string;
  color: string;
  backgroundColor: string;
  description: string;
  actionRequired: boolean;
  calculatedAt: string;
  confidence: number;
}

export interface HealthIndexResponse {
  score: HealthIndexScore | null;
  display?: HealthIndexDisplay;
  message?: string;
}

// Emfit Types

export interface EmfitDevice {
  id: string;
  serial_number: string;
  facility_id: number;
  facility_name?: string;
  resident_id: number;
  resident_name?: string;
  room_number: string;
  last_seen_alive: string | null;
  connection_status: 'connected' | 'disconnected' | 'unknown';
  device_status: 0 | 1 | 2 | 3 | 4; // 0=Undeployed, 1=Absence, 2=Presence, 3=NetworkError, 4=SensorError
  created_at: string;
  updated_at: string;
}

export interface EmfitLiveData {
  id: string;
  device_serial: string;
  timestamp: string;
  heart_rate: number | null;
  respiration_rate: number | null;
  activity: number | null;
  raw_json: any;
  created_at: string;
}

export interface EmfitEvent {
  id: string;
  device_serial: string;
  event_type: number;
  event_name: string;
  timestamp: string;
  alert_generated: boolean;
  alert_id: string | null;
  created_at: string;
}

export interface EmfitSleepSummary {
  id: string;
  emfit_period_id: string;
  device_serial: string;
  sleep_start: string;
  sleep_end: string;
  duration_in_bed: number;
  avg_hr: number | null;
  avg_rr: number | null;
  avg_act: number | null;
  sleep_score: number | null;
  duration_awake: number | null;
  duration_in_sleep: number | null;
  duration_in_rem: number | null;
  duration_in_light: number | null;
  duration_in_deep: number | null;
  duration_sleep_onset: number | null;
  bedexit_count: number | null;
  tossnturn_count: number | null;
  awakenings: number | null;
  min_hr: number | null;
  max_hr: number | null;
  min_rr: number | null;
  max_rr: number | null;
  hrv_rmssd_evening: number | null;
  hrv_rmssd_morning: number | null;
  hrv_score: number | null;
  hrv_lf: number | null;
  hrv_hf: number | null;
  created_at: string;
}

export interface EmfitBackfillLog {
  id: string;
  device_serial: string | null;
  backfill_start: string;
  backfill_end: string;
  periods_processed: number;
  records_created: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface EmfitDashboardStats {
  totalDevices: number;
  activeDevices: number;
  offlineDevices: number;
  recentEvents: number;
  avgSleepScore: number;
  poorSleepCount: number;
}
